/**
 * Cuore One — main storefront interactivity (Dawn-compatible)
 * Vanilla JS, no dependencies. Each feature degrades safely if DOM nodes are absent.
 */
(function () {
  'use strict';

  // --- Shared utilities ---------------------------------------------------

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function on(el, ev, handler, opts) {
    if (!el) {
      return;
    }
    el.addEventListener(ev, handler, opts || false);
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, ms);
    };
  }

  function parseJsonSafe(str, fallback) {
    try {
      return JSON.parse(str);
    } catch (e) {
      return fallback;
    }
  }

  /** Parse JSON bodies from Ajax endpoints; tolerate empty or HTML error bodies. */
  function readAjaxJson(r) {
    return r.text().then(function (text) {
      var body = {};
      if (text) {
        try {
          body = JSON.parse(text);
        } catch (e) {
          body = { message: text.slice(0, 200) };
        }
      }
      return { ok: r.ok, status: r.status, body: body };
    });
  }

  // --- Shared text strings (injected from locale via window.cuoreStrings) ----

  var TEXTS = window.cuoreStrings || {};

  var BANKS = [
    { id: 'master', label: 'Master', value: '-14', color: '#ED2B37', bar: 85 },
    { id: 'sub', label: 'Subwoofer', value: '-6.0', color: '#027BE2', bar: 60 },
    { id: 'rear', label: 'Rear Fill', value: '-18', color: '#22F044', bar: 52 },
    { id: 'digital', label: 'Digital', value: '-9.0', color: '#E02BED', bar: 68 },
  ];

  var DESCS = {
    volume: 'Turn to adjust level. Press to cycle to the next volume bank. Each bank has its own color on the display and ambient ring.',
    menu_root: 'Hold-press opens a menu for quickly switching volume banks or accessing input sources, presets, and Tone settings \u2014 all by turning to scroll and pressing to select',
    voltage: 'High-precision voltage monitor with live line chart. Ambient ring reflects system state: green (normal), yellow (warning), red (critical).',
    bluetooth: 'Playback control and BT pairing. Double click to pair, rotate right for next, rotate left for previous, click to play/pause.',
  };

  /** Strip optional "Ships " prefix; metafields should store date-only text. */
  function normalizeShipDate(str) {
    var value = (str || '').trim();
    if (!value) {
      return '';
    }
    return value.replace(/^ships\s+/i, '').trim();
  }

  function formatShipsLabel(str) {
    var date = normalizeShipDate(str);
    if (!date) {
      return '';
    }
    var template =
      (TEXTS.batchSelector && TEXTS.batchSelector.ships) ||
      (TEXTS.cart && TEXTS.cart.ships) ||
      'Ships {{ date }}';
    return template.replace(/\{\{\s*date\s*\}\}/g, date);
  }

  // --- Cart module --------------------------------------------------------

  var Cart = (function () {
    var drawer;
    var overlay;
    var itemsRoot;
    var errorEl;
    var footerUpsell;
    var noticeEl;
    var closeEls;
    var _mainVariantIds;

    function getAdapterVariantId() {
      if (!drawer) {
        return '';
      }
      var id = drawer.getAttribute('data-adapter-variant-id');
      return id ? String(id).trim() : '';
    }

    function getMainProductHandle() {
      if (!drawer) {
        return '';
      }
      return (drawer.getAttribute('data-main-product-handle') || '').trim();
    }

    function getMainVariantIds() {
      if (_mainVariantIds) {
        return _mainVariantIds;
      }
      var raw = drawer ? drawer.getAttribute('data-main-variant-ids') : '';
      _mainVariantIds = (raw || '')
        .split(',')
        .map(function (part) {
          return part.trim();
        })
        .filter(Boolean);
      return _mainVariantIds;
    }

    function isAdapterLine(line) {
      var adapterId = getAdapterVariantId();
      return !!(adapterId && line && String(line.id) === adapterId);
    }

    function isMainProductLine(line) {
      if (!line || isAdapterLine(line)) {
        return false;
      }
      var mainIds = getMainVariantIds();
      if (mainIds.length && mainIds.indexOf(String(line.id)) !== -1) {
        return true;
      }
      var mainHandle = getMainProductHandle();
      if (mainHandle && line.handle === mainHandle) {
        return true;
      }
      return false;
    }

    function hasMainProductInCart(cart) {
      if (!cart || !Array.isArray(cart.items)) {
        return false;
      }
      return cart.items.some(isMainProductLine);
    }

    function hasAdapterInCart(cart) {
      if (!cart || !Array.isArray(cart.items)) {
        return false;
      }
      return cart.items.some(isAdapterLine);
    }

    function getAdapterLine(cart) {
      if (!cart || !Array.isArray(cart.items)) {
        return null;
      }
      return cart.items.find(isAdapterLine) || null;
    }

    function removeOrphanAdapterFromCart(cart, messageKey) {
      if (!hasAdapterInCart(cart) || hasMainProductInCart(cart)) {
        return Promise.resolve(cart);
      }
      var adapterLine = getAdapterLine(cart);
      if (!adapterLine) {
        return Promise.resolve(cart);
      }
      return changeLineQuantity(adapterLine.key, 0)
        .then(function () {
          var msg =
            (TEXTS.cart && TEXTS.cart[messageKey]) ||
            (TEXTS.cart && TEXTS.cart.adapterRequiresMain) ||
            '';
          if (msg) {
            if (messageKey === 'adapterRemovedOrphan') {
              showNotice(msg);
            } else {
              showError(msg);
            }
          }
          return fetchCart();
        })
        .catch(function () {
          return cart;
        });
    }

    function getSelectedBatchDetails() {
      var btn =
        $('.cuore-batch-option.is-selected') ||
        $('.cuore-batch-option[aria-checked="true"]') ||
        $('.cuore-batch-option:not(.is-disabled):not([disabled])');
      if (!btn) {
        return { shipDate: '', batchLabel: '' };
      }
      return {
        shipDate: normalizeShipDate(btn.getAttribute('data-ship-date') || ''),
        batchLabel: btn.getAttribute('data-batch-label') || '',
      };
    }

    function getShipDateFromCart(cart) {
      if (!cart || !Array.isArray(cart.items)) {
        return '';
      }
      var adapterId = getAdapterVariantId();
      var shipFromCart = '';
      cart.items.forEach(function (line) {
        if (shipFromCart) {
          return;
        }
        if (adapterId && String(line.id) === adapterId) {
          return;
        }
        if (!line.properties) {
          return;
        }
        shipFromCart = normalizeShipDate(line.properties._ship_date || line.properties['_ship_date'] || '');
      });
      return shipFromCart;
    }

    function updateTermsShipDate(shipDate) {
      var value = shipDate || getSelectedBatchDetails().shipDate || '';
      document.querySelectorAll('[data-cuore-terms-ship-date]').forEach(function (el) {
        el.textContent = value;
      });
    }

    function getCartPageRoot() {
      return document.querySelector('.cuore-cart-page');
    }

    function showCartPageNotice(msg) {
      var text = (msg || '').trim();
      if (!text) {
        return;
      }
      try {
        sessionStorage.setItem('cuoreCartPageNotice', text);
      } catch (e) {
        /* ignore */
      }
    }

    function clearCartPageNotice() {
      var root = getCartPageRoot();
      if (!root) {
        return;
      }
      var el = root.querySelector('[data-cuore-cart-page-notice]');
      if (el) {
        el.remove();
      }
    }

    function flushCartPageNotice() {
      var root = getCartPageRoot();
      if (!root) {
        return;
      }
      var msg = '';
      try {
        msg = (sessionStorage.getItem('cuoreCartPageNotice') || '').trim();
        sessionStorage.removeItem('cuoreCartPageNotice');
      } catch (e) {
        /* ignore */
      }
      clearCartPageNotice();
      if (!msg) {
        return;
      }
      var inner = root.querySelector('.cuore-cart-page__inner');
      var title = root.querySelector('.cuore-cart-page__title');
      if (!inner || !title) {
        return;
      }
      var el = document.createElement('div');
      el.className = 'cuore-cart-notice is-visible';
      el.setAttribute('data-cuore-cart-page-notice', '');
      el.setAttribute('role', 'status');
      el.textContent = msg;
      title.insertAdjacentElement('afterend', el);
    }

    function getCartItemCount(cart) {
      if (!cart || typeof cart.item_count !== 'number' || isNaN(cart.item_count)) {
        return null;
      }
      return cart.item_count;
    }

    function shouldReloadCartPage(initialCount, cart, orphanRemoved) {
      if (orphanRemoved) {
        return true;
      }
      var current = getCartItemCount(cart);
      if (current === null) {
        return false;
      }
      if (typeof initialCount !== 'number' || isNaN(initialCount)) {
        return false;
      }
      return current !== initialCount;
    }

    function reloadCartPageIfNeeded(cart, orphanRemoved, opts) {
      var cartPage = getCartPageRoot();
      if (!cartPage) {
        return false;
      }
      var initial = parseInt(cartPage.getAttribute('data-initial-item-count'), 10);
      if (opts && opts.fromCartPage) {
        window.location.reload();
        return true;
      }
      if (shouldReloadCartPage(initial, cart, orphanRemoved)) {
        window.location.reload();
        return true;
      }
      return false;
    }

    function hasPurchasableBatch() {
      var hero = document.querySelector('[data-cuore-hero]');
      if (hero && hero.hasAttribute('data-cuore-has-purchasable-batch')) {
        return hero.getAttribute('data-cuore-has-purchasable-batch') === 'true';
      }
      return !!$('.cuore-batch-option:not(.is-disabled):not([disabled]):not(.cuore-batch-option--sold-out):not(.cuore-batch-option--soon)');
    }

    function syncPreorderButtons() {
      var canPurchase = hasPurchasableBatch();
      $$('[data-cuore-action="preorder"]').forEach(function (btn) {
        btn.disabled = !canPurchase;
        if (!canPurchase) {
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.removeAttribute('aria-disabled');
        }
      });
    }

    function getSelectedVariantId() {
      var selected = $('.cuore-batch-option.is-selected');
      if (selected && !selected.disabled && !selected.classList.contains('is-disabled')) {
        if (selected.hasAttribute('data-variant-id')) {
          return String(selected.getAttribute('data-variant-id')).trim();
        }
      }
      selected = $('.cuore-batch-option[aria-checked="true"]');
      if (selected && !selected.disabled && !selected.classList.contains('is-disabled')) {
        if (selected.hasAttribute('data-variant-id')) {
          return String(selected.getAttribute('data-variant-id')).trim();
        }
      }
      var first = $('.cuore-batch-option:not(.is-disabled):not([disabled]):not(.cuore-batch-option--sold-out):not(.cuore-batch-option--soon)');
      if (first && first.hasAttribute('data-variant-id')) {
        return String(first.getAttribute('data-variant-id')).trim();
      }
      return '';
    }

    function setOpen(open) {
      if (drawer) {
        drawer.classList.toggle('is-open', open);
      }
      if (overlay) {
        overlay.classList.toggle('is-open', open);
      }
      if (open) {
        document.documentElement.classList.add('cuore-cart-open');
      } else {
        document.documentElement.classList.remove('cuore-cart-open');
      }
    }

    function clearError() {
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.classList.remove('is-visible');
        errorEl.hidden = true;
      }
      if (noticeEl) {
        noticeEl.textContent = '';
        noticeEl.classList.remove('is-visible');
        noticeEl.hidden = true;
      }
    }

    function showError(msg) {
      if (!errorEl || !msg) {
        return;
      }
      if (noticeEl) {
        noticeEl.textContent = '';
        noticeEl.classList.remove('is-visible');
        noticeEl.hidden = true;
      }
      errorEl.textContent = msg;
      errorEl.classList.add('is-visible');
      errorEl.hidden = false;
    }

    function showNotice(msg) {
      if (!noticeEl || !msg) {
        return;
      }
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.classList.remove('is-visible');
        errorEl.hidden = true;
      }
      noticeEl.textContent = msg;
      noticeEl.classList.add('is-visible');
      noticeEl.hidden = false;
    }

    function formatMoney(cents) {
      var currency =
        (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
      var locale =
        (window.Shopify && Shopify.locale) || document.documentElement.lang || undefined;
      return (cents / 100).toLocaleString(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    function updateAdapterUpsellPrice() {
      if (!drawer) {
        return;
      }
      var cents = parseInt(drawer.getAttribute('data-adapter-price-cents') || '', 10);
      if (!cents) {
        return;
      }
      var formatted = formatMoney(cents);
      $$('[data-cuore-adapter-price]', drawer).forEach(function (priceEl) {
        priceEl.textContent = formatted;
      });
    }

    function setAdapterTogglesDisabled(disabled) {
      if (!drawer) {
        return;
      }
      $$('[data-cuore-adapter-toggle]', drawer).forEach(function (btn) {
        btn.disabled = disabled;
      });
    }

    function updateTotal(cart) {
      if (!cart || typeof cart.total_price !== 'number') {
        return;
      }
      var formatted = formatMoney(cart.total_price);
      document.querySelectorAll('.cuore-cart-footer__total, [data-cuore-cart-page-total]').forEach(function (el) {
        el.textContent = formatted;
      });
    }

    function renderCart(cart) {
      if (!itemsRoot || !cart || !Array.isArray(cart.items)) {
        return;
      }
      var count = cart.item_count || 0;
      document.querySelectorAll('[data-cuore-header-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.style.opacity = count === 0 ? '0' : '';
      });
      itemsRoot.innerHTML = '';
      if (cart.items.length === 0) {
        var empty = document.createElement('p');
        empty.className = 'cuore-cart-drawer__empty';
        empty.textContent = TEXTS.cart.empty;
        itemsRoot.appendChild(empty);
        if (drawer.dataset.cuoreDrawerContext === 'global') {
          var shopLink = document.createElement('a');
          shopLink.href = '/';
          shopLink.className = 'cuore-cart-drawer__shop-link';
          shopLink.textContent = TEXTS.cart.shopLink;
          itemsRoot.appendChild(shopLink);
        }
        var emptyFooter = $('.cuore-cart-footer', drawer);
        if (emptyFooter) emptyFooter.hidden = true;
        syncAdapterToggleState(cart);
        updateTotal(cart);
        var cbBtn = $('[data-cuore-action="checkout"]', drawer);
        var cbTerms = $('[data-cuore-terms-checkbox]', drawer);
        if (cbTerms) cbTerms.checked = false;
        if (cbBtn) cbBtn.disabled = true;
        updateTermsShipDate(getShipDateFromCart(cart));
        return;
      }
      var filledFooter = $('.cuore-cart-footer', drawer);
      if (filledFooter) filledFooter.hidden = false;
      cart.items.forEach(function (line) {
        var row = document.createElement('div');
        row.className = 'cuore-cart-line';
        row.dataset.lineKey = line.key;

        var thumb = document.createElement('div');
        thumb.className = 'cuore-cart-line__thumb';
        if (line.image) {
          var img = document.createElement('img');
          img.src = line.image.replace(/(\.\w+)(\?|$)/, '_180x$1$2');
          img.alt = line.product_title || '';
          img.loading = 'lazy';
          thumb.appendChild(img);
        } else {
          thumb.innerHTML = '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="#333" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>';
          thumb.classList.add('cuore-cart-line__thumb--placeholder');
        }
        row.appendChild(thumb);

        var info = document.createElement('div');
        info.className = 'cuore-cart-line__info';

        var titleRow = document.createElement('div');
        titleRow.className = 'cuore-cart-line__title-row';
        var title = document.createElement('div');
        title.className = 'cuore-cart-line__title';
        title.textContent = line.product_title || '';
        var price = document.createElement('div');
        price.className = 'cuore-cart-line__price';
        price.textContent = line.final_line_price ? formatMoney(line.final_line_price) : '';
        titleRow.appendChild(title);
        titleRow.appendChild(price);
        info.appendChild(titleRow);

        var variant = line.variant_title || '';
        var subtitleParts = [];
        if (line.product_type) subtitleParts.push(line.product_type);
        if (variant) subtitleParts.push(variant);
        var subtitleText = subtitleParts.join(' · ');
        if (subtitleText) {
          var subtitle = document.createElement('div');
          subtitle.className = 'cuore-cart-line__meta';
          subtitle.textContent = subtitleText;
          info.appendChild(subtitle);
        }

        var qtyRow = document.createElement('div');
        qtyRow.className = 'cuore-cart-line__qty-row';
        var qtyMinus = document.createElement('button');
        qtyMinus.type = 'button';
        qtyMinus.className = 'cuore-cart-line__qty-btn';
        qtyMinus.textContent = '−';
        qtyMinus.setAttribute('aria-label', 'Decrease quantity');
        var qtyInput = document.createElement('input');
        qtyInput.type = 'number';
        qtyInput.className = 'cuore-cart-line__qty-input';
        qtyInput.value = line.quantity || 1;
        qtyInput.min = '0';
        qtyInput.max = '99';
        qtyInput.setAttribute('aria-label', 'Quantity');
        var qtyPlus = document.createElement('button');
        qtyPlus.type = 'button';
        qtyPlus.className = 'cuore-cart-line__qty-btn';
        qtyPlus.textContent = '+';
        qtyPlus.setAttribute('aria-label', 'Increase quantity');

        var lineKey = line.key;
        function updateQty(newQty) {
          newQty = Math.max(0, Math.min(99, newQty));
          qtyMinus.disabled = true;
          qtyPlus.disabled = true;
          qtyInput.disabled = true;
          clearError();
          changeLineQuantity(lineKey, newQty)
            .then(function () { return refreshAndRender(); })
            .catch(function (err) {
              var msg = (err && err.body && err.body.description) ||
                        (err && err.message) || TEXTS.cart.updateError;
              showError(msg);
              refreshAndRender();
            });
        }
        qtyMinus.addEventListener('click', function () {
          updateQty(parseInt(qtyInput.value, 10) - 1);
        });
        qtyPlus.addEventListener('click', function () {
          updateQty(parseInt(qtyInput.value, 10) + 1);
        });
        qtyInput.addEventListener('change', function () {
          updateQty(parseInt(qtyInput.value, 10) || 0);
        });

        qtyRow.appendChild(qtyMinus);
        qtyRow.appendChild(qtyInput);
        qtyRow.appendChild(qtyPlus);

        var removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'cuore-cart-line__remove';
        removeBtn.setAttribute('aria-label', 'Remove ' + (line.product_title || 'item'));
        removeBtn.textContent = TEXTS.cart.remove;
        removeBtn.addEventListener('click', function () {
          removeBtn.disabled = true;
          clearError();
          changeLineQuantity(lineKey, 0)
            .then(function () { return refreshAndRender(); })
            .catch(function (err) {
              removeBtn.disabled = false;
              var msg = (err && err.body && err.body.description) || (err && err.message) || '';
              if (msg) showError(msg);
            });
        });

        var actionsRow = document.createElement('div');
        actionsRow.className = 'cuore-cart-line__actions';
        actionsRow.appendChild(qtyRow);
        actionsRow.appendChild(removeBtn);
        info.appendChild(actionsRow);

        var shipProp = line.properties && line.properties._ship_date;
        var selectedBtn = document.querySelector('.cuore-batch-option.is-selected');
        var shipDate = normalizeShipDate(shipProp || (selectedBtn && selectedBtn.getAttribute('data-ship-date')) || '');
        if (shipDate) {
          var badge = document.createElement('div');
          badge.className = 'cuore-cart-line__ship-badge';
          badge.textContent = formatShipsLabel(shipDate);
          info.appendChild(badge);
        }

        row.appendChild(info);
        itemsRoot.appendChild(row);
      });
      syncAdapterToggleState(cart);
      updateTotal(cart);
      updateTermsShipDate(getShipDateFromCart(cart));
    }

    var _adapterInCart = false;

    function syncAdapterToggleState(cart) {
      var adapterId = getAdapterVariantId();
      var prompt = $('[data-cuore-adapter-prompt]', drawer);
      var promptAction = $('[data-cuore-adapter-prompt-action]', drawer);
      var offerEl = $('[data-cuore-adapter-offer]', drawer);
      var confirmEl = $('[data-cuore-adapter-confirm]', drawer);
      var hasMain = hasMainProductInCart(cart);
      var hideUpsell = drawer && drawer.getAttribute('data-hide-adapter-upsell') === 'true';

      if (prompt) {
        prompt.hidden = !hasMain || hideUpsell;
      }

      _adapterInCart = false;
      if (adapterId && hasMain && cart && Array.isArray(cart.items)) {
        _adapterInCart = cart.items.some(function (item) {
          return String(item.id) === adapterId;
        });
      }

      var showAdapterSection = hasMain && adapterId && !hideUpsell;

      if (promptAction) {
        promptAction.hidden = !showAdapterSection;
      }
      if (offerEl) {
        offerEl.hidden = _adapterInCart;
      }
      if (confirmEl) {
        confirmEl.hidden = !_adapterInCart;
      }
      if (showAdapterSection && !_adapterInCart) {
        updateAdapterUpsellPrice();
      }

      if (footerUpsell) {
        footerUpsell.hidden = !hasMain || !adapterId || hideUpsell || _adapterInCart;
        footerUpsell.classList.remove('is-active');
      }

      var toggles = $$('[data-cuore-adapter-toggle]', drawer);
      if (!toggles.length) {
        return;
      }

      if (!adapterId || !hasMain || hideUpsell) {
        toggles.forEach(function (btn) {
          btn.disabled = true;
          btn.classList.remove('is-active');
        });
        return;
      }

      toggles.forEach(function (btn) {
        var labelEl = btn.querySelector('[data-cuore-adapter-label]');
        btn.disabled = false;
        btn.classList.remove('is-active');
        if (labelEl) {
          labelEl.textContent = TEXTS.cart.adapterUpsellAdd || TEXTS.cart.adapterAdd;
        }
      });
    }

    function fetchCart() {
      return fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then(function (r) {
          if (!r.ok) {
            throw new Error('Cart fetch failed');
          }
          return r.json();
        })
        .catch(function () {
          return { items: [] };
        });
    }

    function refreshAndRender(opts) {
      return fetchCart()
        .then(function (cart) {
          var beforeOrphan = getCartItemCount(cart);
          return removeOrphanAdapterFromCart(cart, 'adapterRemovedOrphan').then(function (updated) {
            var afterOrphan = getCartItemCount(updated);
            var orphanRemoved =
              beforeOrphan !== null && afterOrphan !== null && afterOrphan < beforeOrphan;
            if (orphanRemoved) {
              showCartPageNotice(TEXTS.cart && TEXTS.cart.adapterRemovedOrphan);
            }
            return { cart: updated, orphanRemoved: orphanRemoved };
          });
        })
        .then(function (result) {
          if (reloadCartPageIfNeeded(result.cart, result.orphanRemoved, opts)) {
            return result.cart;
          }
          renderCart(result.cart);
          return result.cart;
        })
        .catch(function () {
          return fetchCart().then(function (cart) {
            if (!reloadCartPageIfNeeded(cart, false, opts)) {
              renderCart(cart);
            }
            return cart;
          });
        });
    }

    /** Shopify cart Ajax endpoints may return HTTP 200 with { status: 422 } in the JSON body. */
    function assertCartApiSuccess(res, fallbackMessage) {
      var bodyStatus = res.body && res.body.status;
      if (!res.ok || bodyStatus) {
        var err = new Error(
          (res.body && (res.body.description || res.body.message)) || fallbackMessage
        );
        err.status = bodyStatus || res.status;
        err.body = res.body;
        throw err;
      }
      return res.body;
    }

    function showCartMutationError(err) {
      var msg = (err && err.body && (err.body.description || err.body.message)) || '';
      if (err.status === 422) {
        showError(msg || TEXTS.cart.unavailable);
      } else if (err.status === 429) {
        showError(TEXTS.cart.tooManyRequests);
      } else {
        showError(msg || TEXTS.cart.genericError);
      }
    }

    function addVariant(variantId, qty, properties) {
      var payload = { id: variantId, quantity: qty || 1 };
      if (properties && Object.keys(properties).length) {
        payload.properties = properties;
      }
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      })
        .then(readAjaxJson)
        .then(function (res) {
          return assertCartApiSuccess(res, 'Add to cart failed');
        });
    }

    function changeLineQuantity(lineKey, quantity) {
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ id: lineKey, quantity: quantity }),
      })
        .then(readAjaxJson)
        .then(function (res) {
          return assertCartApiSuccess(res, 'Cart update failed');
        });
    }

    var _addingToCart = false;

    function addToCartAndOpen() {
      if (_addingToCart) return;
      clearError();
      if (!hasPurchasableBatch()) {
        showError(TEXTS.cart.unavailable);
        return;
      }
      var vid = getSelectedVariantId();
      if (!vid) {
        showError(TEXTS.cart.selectBatch);
        setOpen(true);
        return;
      }
      _addingToCart = true;
      var btn = document.querySelector('[data-cuore-action="preorder"]');
      if (btn) btn.disabled = true;

      var batch = getSelectedBatchDetails();
      var properties = {};
      if (batch.shipDate) {
        properties._ship_date = batch.shipDate;
      }
      if (batch.batchLabel) {
        properties._batch_label = batch.batchLabel;
      }

      addVariant(vid, 1, properties)
        .then(function () {
          return refreshAndRender();
        })
        .then(function () {
          setOpen(true);
        })
        .catch(function (err) {
          showCartMutationError(err);
          setOpen(true);
        })
        .then(function () {
          _addingToCart = false;
          if (btn) btn.disabled = false;
        });
    }

    function isPreOrderClick(target) {
      if (!target) {
        return false;
      }
      var el = target.closest(
        '[data-cuore-action="preorder"], [data-cuore-action="add-cart"], .cuore-btn-preorder, .cuore-preorder-now'
      );
      if (el) {
        return true;
      }
      el = target.closest('button, a, [role="button"]');
      if (!el) {
        return false;
      }
      var t = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (t.indexOf('pre-order now') !== -1 || /\bpre-order\b/i.test(t)) {
        return true;
      }
      if (t.indexOf('reserve now') !== -1) {
        return false;
      }
      if (/\breserve\b/.test(t)) {
        return true;
      }
      return false;
    }

    function isReserveCheckoutClick(target) {
      if (!target) {
        return false;
      }
      var el = target.closest('[data-cuore-action="checkout"], .cuore-btn-reserve-now, .cuore-reserve-now');
      if (el) {
        return true;
      }
      el = target.closest('button, a, [role="button"]');
      if (!el) {
        return false;
      }
      var t = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      return t === 'reserve now' || t.indexOf('reserve now') !== -1;
    }

    function onDocumentClick(e) {
      if (e.target.closest('[data-cuore-cart-open]')) {
        e.preventDefault();
        setOpen(true);
        return;
      }
      if (isReserveCheckoutClick(e.target)) {
        var btn = e.target.closest('[data-cuore-action="checkout"]') || e.target;
        if (btn.disabled) return;
        e.preventDefault();
        fetchCart()
          .then(function (cart) {
            if (hasAdapterInCart(cart) && !hasMainProductInCart(cart)) {
              showError(TEXTS.cart.adapterRequiresMain);
              return removeOrphanAdapterFromCart(cart, 'adapterRequiresMain').then(function (nextCart) {
                renderCart(nextCart);
              });
            }
            window.location = '/checkout';
          })
          .catch(function () {
            showError(TEXTS.cart.genericError);
          });
        return;
      }
      if (isPreOrderClick(e.target)) {
        e.preventDefault();
        var preorderBtn = e.target.closest('[data-cuore-action="preorder"]');
        if (preorderBtn && preorderBtn.disabled) {
          return;
        }
        if (!hasPurchasableBatch()) {
          showError(TEXTS.cart.unavailable);
          return;
        }
        addToCartAndOpen();
      }
    }

    function onAdapterClick() {
      var adapterId = getAdapterVariantId();
      if (!adapterId) return;
      var want = !_adapterInCart;
      setAdapterTogglesDisabled(true);
      clearError();
      fetchCart()
        .then(function (cart) {
          var existing = cart.items.find(function (item) {
            return String(item.id) === adapterId;
          });
          if (want && !existing) {
            if (!hasMainProductInCart(cart)) {
              showError(TEXTS.cart.adapterRequiresMain);
              return null;
            }
            return addVariant(adapterId, 1);
          }
          if (!want && existing) {
            return changeLineQuantity(existing.key, 0);
          }
          return null;
        })
        .then(function () {
          return refreshAndRender();
        })
        .catch(function (err) {
          showCartMutationError(err);
          return refreshAndRender();
        });
    }

    function bindClose() {
      closeEls.forEach(function (btn) {
        on(btn, 'click', function (e) {
          e.preventDefault();
          setOpen(false);
        });
      });
      on(overlay, 'click', function () {
        setOpen(false);
      });
    }

    function initCartPage() {
      var cartPage = getCartPageRoot();
      var form = document.getElementById('CuoreCartForm');
      if (!cartPage || !form) {
        return;
      }

      function applyCartPageUpdate(lineKey, qty) {
        clearError();
        return changeLineQuantity(lineKey, qty)
          .then(function () {
            return refreshAndRender({ fromCartPage: true });
          })
          .catch(function (err) {
            var msg =
              (err && err.body && err.body.description) ||
              (err && err.message) ||
              TEXTS.cart.updateError;
            showError(msg);
            // Omit fromCartPage here: the mutation failed so item counts did not
            // change. Passing fromCartPage would unconditionally reload the page
            // and erase the error before the user can read it.
            return refreshAndRender();
          });
      }

      form.querySelectorAll('[data-cuore-cart-page-remove]').forEach(function (btn) {
        on(btn, 'click', function (e) {
          e.preventDefault();
          var lineKey = btn.getAttribute('data-line-key');
          if (!lineKey) {
            return;
          }
          btn.disabled = true;
          applyCartPageUpdate(lineKey, 0);
        });
      });

      form.querySelectorAll('[data-cuore-cart-page-qty-dec], [data-cuore-cart-page-qty-inc]').forEach(function (btn) {
        on(btn, 'click', function (e) {
          e.preventDefault();
          var idx =
            btn.getAttribute('data-cuore-cart-page-qty-dec') ||
            btn.getAttribute('data-cuore-cart-page-qty-inc');
          var input = form.querySelector('[data-cuore-cart-page-qty="' + idx + '"]');
          if (!input) {
            return;
          }
          var row = input.closest('[data-line-key]');
          var lineKey = row && row.getAttribute('data-line-key');
          if (!lineKey) {
            return;
          }
          var val = parseInt(input.value, 10) || 0;
          var isInc = btn.hasAttribute('data-cuore-cart-page-qty-inc');
          var next = isInc ? val + 1 : Math.max(0, val - 1);
          btn.disabled = true;
          applyCartPageUpdate(lineKey, next);
        });
      });

      form.querySelectorAll('[data-cuore-cart-page-qty]').forEach(function (input) {
        on(input, 'change', function () {
          var row = input.closest('[data-line-key]');
          var lineKey = row && row.getAttribute('data-line-key');
          if (!lineKey) {
            return;
          }
          var next = Math.max(0, parseInt(input.value, 10) || 0);
          input.disabled = true;
          applyCartPageUpdate(lineKey, next);
        });
      });
    }

    function init() {
      drawer = $('.cuore-cart-drawer');
      overlay = $('.cuore-cart-overlay');
      if (!drawer) {
        on(document, 'click', onDocumentClick);
        return;
      }
      itemsRoot = $('[data-cuore-cart-items]', drawer) || $('.cuore-cart-drawer__items', drawer);
      noticeEl = $('[data-cuore-cart-notice]', drawer) || $('.cuore-cart-notice', drawer);
      errorEl = $('[data-cuore-cart-error]', drawer) || $('.cuore-cart-drawer__error', drawer);
      footerUpsell = $('[data-cuore-footer-upsell]', drawer);
      closeEls = $$('[data-cuore-cart-close]', drawer).concat(
        $$('.cuore-cart-drawer__close', drawer)
      );

      on(document, 'click', onDocumentClick);
      bindClose();
      $$('[data-cuore-adapter-toggle]', drawer).forEach(function (btn) {
        on(btn, 'click', onAdapterClick);
      });
      updateAdapterUpsellPrice();

      var termsCheckbox = $('[data-cuore-terms-checkbox]', drawer);
      var checkoutBtn = $('[data-cuore-action="checkout"]', drawer);
      if (checkoutBtn) {
        var updateCheckoutGate = function () {
          var hasItems = itemsRoot && itemsRoot.querySelectorAll('.cuore-cart-line').length > 0;
          var termsOk = !termsCheckbox || termsCheckbox.checked;
          checkoutBtn.disabled = !termsOk || !hasItems;
        };
        if (termsCheckbox) on(termsCheckbox, 'change', updateCheckoutGate);
      }

      document.addEventListener('cuore:batch-change', function (e) {
        var ship = normalizeShipDate((e.detail && e.detail.shipDate) || '');
        updateTermsShipDate(ship);
      });

      if (getCartPageRoot()) {
        flushCartPageNotice();
      }
      initCartPage();
      refreshAndRender();
    }

    return {
      init: init,
      refresh: refreshAndRender,
      open: function () {
        setOpen(true);
      },
      addAndOpen: addToCartAndOpen,
      syncPreorderButtons: syncPreorderButtons,
    };
  })();

  // --- Batch selector -----------------------------------------------------

  var BatchSelector = (function () {
    function isDisabled(btn) {
      if (!btn) {
        return true;
      }
      if (btn.disabled || btn.classList.contains('is-disabled') || btn.getAttribute('aria-disabled') === 'true') {
        return true;
      }
      return btn.classList.contains('cuore-batch-option--sold-out') || btn.classList.contains('cuore-batch-option--soon');
    }

    function readPriceFromBtn(btn) {
      var p = btn.getAttribute('data-price');
      if (p) {
        return p;
      }
      p = btn.getAttribute('data-price-formatted');
      return p || '';
    }

    function syncRadioDot(btn) {
      var radio = btn.querySelector('.cuore-batch-option__radio');
      if (!radio) {
        return;
      }
      var dot = radio.querySelector('.cuore-batch-option__radio-dot');
      if (btn.classList.contains('is-selected')) {
        if (!dot) {
          dot = document.createElement('span');
          dot.className = 'cuore-batch-option__radio-dot';
          dot.setAttribute('aria-hidden', 'true');
          radio.appendChild(dot);
        }
      } else if (dot) {
        dot.remove();
      }
    }

    function applySelection(btn) {
      var group = btn.closest('.cuore-batch');
      var scope = group || document;
      $$('.cuore-batch-option', scope).forEach(function (b) {
        b.classList.remove('is-selected');
        b.setAttribute('aria-checked', 'false');
        syncRadioDot(b);
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-checked', 'true');
      syncRadioDot(btn);

      var vid = btn.getAttribute('data-variant-id') || '';
      var ship = normalizeShipDate(btn.getAttribute('data-ship-date') || '');
      var label = btn.getAttribute('data-batch-label') || '';
      var price = readPriceFromBtn(btn);

      $$('[data-cuore-cta-label]').forEach(function (el) {
        el.textContent = btn.getAttribute('data-cta-label') || el.getAttribute('data-default-cta') || el.textContent;
      });
      var priceEls = $$('[data-cuore-cta-price], .cuore-cta-price');
      priceEls.forEach(function (el) {
        if (price) {
          el.textContent = price;
        }
      });
      $$('[data-cuore-final-batch-info], .cuore-final-cta__batch').forEach(function (el) {
        var parts = [];
        if (label) {
          parts.push(label);
        }
        if (ship) {
          parts.push(formatShipsLabel(ship));
        }
        el.textContent = parts.join(' · ') || el.getAttribute('data-placeholder') || '';
      });
      document.dispatchEvent(
        new CustomEvent('cuore:batch-change', {
          detail: { variantId: vid, shipDate: ship, batchLabel: label, price: price },
        })
      );
    }

    function onOptionClick(e) {
      var btn = e.target.closest('.cuore-batch-option');
      if (!btn || isDisabled(btn)) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      applySelection(btn);
    }

    function init() {
      $$('.cuore-batch').forEach(function (group) {
        var opts = $$('.cuore-batch-option', group);
        if (opts.length === 0) {
          return;
        }
        opts.forEach(function (btn) {
          on(btn, 'click', onOptionClick);
          if (btn.getAttribute('role') === 'radio' && !btn.hasAttribute('tabindex')) {
            btn.setAttribute('tabindex', '0');
          }
        });

        var urlVariantId = new URLSearchParams(window.location.search).get('variant');
        var urlTarget = urlVariantId && opts.find(function (b) {
          return b.getAttribute('data-variant-id') === urlVariantId && !isDisabled(b);
        });
        if (urlTarget) {
          applySelection(urlTarget);
          return;
        }

        var current = $('.cuore-batch-option.is-selected', group) || $('.cuore-batch-option[aria-checked="true"]', group);
        if (current && !isDisabled(current)) {
          applySelection(current);
        } else {
          var first = opts.find(function (b) {
            return !isDisabled(b);
          });
          if (first) {
            applySelection(first);
          }
        }
      });
    }

    return { init: init };
  })();

  // --- Notify form --------------------------------------------------------

  var NotifyForm = (function () {
    function bindOne(form) {
      on(form, 'submit', function () {
        try {
          sessionStorage.setItem('cuoreNotifyFormId', JSON.stringify({ id: form.id, ts: Date.now() }));
        } catch (_) {}
      });
    }

    // Other Shopify customer forms (newsletter / contact signups) share the
    // customer_posted=true redirect but aren't notify forms. Submitting one
    // means a pending notify record (e.g. a notify submit whose hCaptcha was
    // abandoned) is no longer what's being posted, so drop it — otherwise that
    // stale id could light up a notify success when the unrelated signup
    // returns. Detected generically via the form_type=customer hidden input
    // that {% form 'customer' %} always emits.
    function bindNonNotifyCustomerForms() {
      $$('form').forEach(function (form) {
        if (form.classList.contains('cuore-notify-form')) {
          return;
        }
        var marker = form.querySelector('input[name="form_type"]');
        if (marker && marker.value === 'customer') {
          on(form, 'submit', function () {
            try {
              sessionStorage.removeItem('cuoreNotifyFormId');
            } catch (_) {}
          });
        }
      });
    }

    function resetAllNotifyForms() {
      $$('.cuore-notify-group').forEach(function (wrap) {
        var fieldsEl = wrap.querySelector('[data-cuore-notify-fields]');
        var successEl = wrap.querySelector('[data-cuore-notify-success]');
        if (fieldsEl) fieldsEl.hidden = false;
        if (successEl) successEl.hidden = true;
      });
    }

    // Shopify's {% form 'customer' %} can't tell us which form was submitted:
    // form.posted_successfully? is true for *every* customer form on the page
    // once any one of them posts (the form object carries no id), so it can't
    // drive per-form success. We resolve the posted form on the client instead
    // — via the #fragment Shopify appended (handed over by theme.liquid), then
    // the timestamped record written by the submit handler below. Both verify
    // the id resolves to a real .cuore-notify-form, so an unrelated customer
    // form (e.g. a newsletter signup elsewhere on the page) can never light up
    // a notify form's success message.
    function resolvePostedForm(hash, savedFormId) {
      // The redirect fragment, when present, names the form that was actually
      // posted. If it's one of ours, that's the answer; if it points at a
      // different (non-notify) customer form, that form was submitted — so we
      // must not fall back to a possibly-stale saved id.
      var byHash = hash && hash.charAt(0) === '#' ? document.getElementById(hash.slice(1)) : null;
      if (byHash) {
        return byHash.classList.contains('cuore-notify-form') ? byHash : null;
      }
      if (savedFormId) {
        var byId = document.getElementById(savedFormId);
        if (byId && byId.classList.contains('cuore-notify-form')) {
          return byId;
        }
      }
      return null;
    }

    function handlePostedRedirect() {
      if (location.search.indexOf('customer_posted=true') === -1) {
        return;
      }

      resetAllNotifyForms();

      var hash = '';
      var savedFormId = '';
      try {
        hash = sessionStorage.getItem('cuoreNotifyHash') || location.hash;
        sessionStorage.removeItem('cuoreNotifyHash');
        var rawFormId = sessionStorage.getItem('cuoreNotifyFormId') || '';
        sessionStorage.removeItem('cuoreNotifyFormId');
        if (rawFormId) {
          try {
            var parsed = JSON.parse(rawFormId);
            // Generous window so a slow interactive hCaptcha challenge between
            // submit and redirect doesn't drop the record (was 60s).
            if (parsed && parsed.id && (Date.now() - parsed.ts) < 900000) {
              savedFormId = parsed.id;
            }
          } catch (_) {
            savedFormId = rawFormId;
          }
        }
      } catch (_) {
        hash = location.hash;
      }

      var form = resolvePostedForm(hash, savedFormId);
      if (!form) {
        return;
      }

      var wrap = form.closest('.cuore-notify-group') || form.parentElement;
      if (wrap) {
        var fieldsEl = wrap.querySelector('[data-cuore-notify-fields]');
        var successEl = wrap.querySelector('[data-cuore-notify-success]');
        if (fieldsEl) fieldsEl.hidden = true;
        if (successEl) successEl.hidden = false;
      }

      if (form.id === 'CuoreNotifyHero') {
        window.scrollTo(0, 0);
      } else {
        var target = wrap || form;
        if (target && target.scrollIntoView) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      var url = new URL(window.location.href);
      url.searchParams.delete('customer_posted');
      history.replaceState(null, '', url.pathname + url.search + url.hash);
    }

    function init() {
      bindNonNotifyCustomerForms();
      var forms = $$('.cuore-notify-form');
      if (!forms.length) {
        return;
      }
      forms.forEach(bindOne);
      handlePostedRedirect();
    }

    return { init: init };
  })();

  // --- Fade-in on scroll --------------------------------------------------

  var FadeIn = (function () {
    function init() {
      var nodes = $$('.cuore-fade-in');
      if (!nodes.length || !('IntersectionObserver' in window)) {
        nodes.forEach(function (n) {
          n.classList.add('is-visible');
        });
        return;
      }
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) {
              en.target.classList.add('is-visible');
              io.unobserve(en.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px 80px 0px' }
      );
      nodes.forEach(function (n) {
        io.observe(n);
      });
    }

    return { init: init };
  })();

  // --- Sticky header ------------------------------------------------------

  var StickyHeader = (function () {
    var heroSentinel;

    function update() {
      var header = $('.cuore-sticky-header');
      if (!header) {
        return;
      }
      var threshold = window.innerHeight;
      if (heroSentinel) {
        var rect = heroSentinel.getBoundingClientRect();
        var past = rect.bottom <= 0 || window.scrollY >= threshold;
        header.classList.toggle('is-visible', past && window.scrollY > 40);
      } else {
        header.classList.toggle('is-visible', window.scrollY >= threshold);
      }
      if (window.scrollY <= 8) {
        header.classList.remove('is-visible');
      }
    }

    function init() {
      var header = $('.cuore-sticky-header');
      if (!header) {
        return;
      }
      heroSentinel = $('.cuore-hero') || $('.cuore-hero-section') || $('[data-cuore-hero]');
      on(window, 'scroll', debounce(update, 10), { passive: true });
      on(window, 'resize', debounce(update, 50));
      update();
    }

    return { init: init };
  })();

  // --- Compatibility search ---------------------------------------------

  var CompatSearch = (function () {
    var WORD_TO_NUM = {
      zero: '0', one: '1', two: '2', three: '3', four: '4', five: '5',
      six: '6', seven: '7', eight: '8', nine: '9', ten: '10', eleven: '11',
      twelve: '12', thirteen: '13', fourteen: '14', fifteen: '15',
      sixteen: '16', seventeen: '17', eighteen: '18', nineteen: '19',
      twenty: '20'
    };
    var NUM_TO_WORD = {};
    Object.keys(WORD_TO_NUM).forEach(function (w) { NUM_TO_WORD[WORD_TO_NUM[w]] = w; });

    function tokenize(str) {
      return (str || '').toLowerCase()
        .replace(/[.\-\/\u2013\u2014]+/g, ' ')
        .replace(/([a-z])(\d)/g, '$1 $2')
        .replace(/(\d)([a-z])/g, '$1 $2')
        .split(/\s+/)
        .filter(Boolean);
    }

    function addNumberAliases(tokens) {
      var out = [];
      tokens.forEach(function (t) {
        out.push(t);
        if (WORD_TO_NUM[t]) out.push(WORD_TO_NUM[t]);
        else if (NUM_TO_WORD[t]) out.push(NUM_TO_WORD[t]);
      });
      return out;
    }

    function buildSearchable(brand, model) {
      return addNumberAliases(tokenize(brand + ' ' + model)).join(' ');
    }

    function matchesQuery(searchable, queryTokens) {
      return queryTokens.every(function (qt) {
        return searchable.indexOf(qt) !== -1;
      });
    }

    function getModels(holder) {
      if (!holder) {
        holder =
          $('.cuore-dsp-compat') ||
          $('.cuore-compatibility') ||
          (($('.cuore-dsp-search') && $('.cuore-dsp-search').closest('[data-dsp-models]')) || null);
      }
      if (!holder) {
        return [];
      }
      var raw = holder.getAttribute('data-dsp-models');
      if (raw) {
        var parsed = parseJsonSafe(raw, null);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
      var script = document.getElementById('cuore-dsp-models-json');
      if (script && script.textContent) {
        var p2 = parseJsonSafe(script.textContent, []);
        return Array.isArray(p2) ? p2 : [];
      }
      return [];
    }

    function prepareIndex(models) {
      return models.map(function (m) {
        var brand = (m && m.brand) || '';
        var model = (m && m.model) || (m && m.name) || '';
        return { data: m, searchable: buildSearchable(brand, model) };
      });
    }

    function renderResults(container, index, query) {
      container.innerHTML = '';
      var raw = (query || '').trim();
      if (raw.length < 2) {
        return;
      }
      var qTokens = addNumberAliases(tokenize(raw));
      if (!qTokens.length) {
        return;
      }
      var list = index.filter(function (entry) {
        return matchesQuery(entry.searchable, qTokens);
      });
      if (!list.length) {
        var empty = document.createElement('div');
        empty.className = 'cuore-dsp-search-empty';
        empty.textContent = TEXTS.compat.noMatch;
        container.appendChild(empty);
        return;
      }
      list.forEach(function (entry) {
        var m = entry.data;
        var isScp = m && (m.scp || m.type === 'scp');
        var row = document.createElement('div');
        row.className = 'cuore-dsp-result ' + (isScp ? 'cuore-dsp-result--scp' : 'cuore-dsp-result--ci');
        var brand = document.createElement('span');
        brand.className = 'cuore-dsp-result__brand';
        brand.textContent = (m && m.brand) || '';
        row.appendChild(brand);
        var name = document.createElement('span');
        name.className = 'cuore-dsp-result__name';
        name.textContent = (m && m.model) || (m && m.name) || '';
        row.appendChild(name);
        var typeBadge = document.createElement('span');
        typeBadge.className = 'cuore-dsp-result__type ' + (isScp ? 'cuore-dsp-result__type--scp' : 'cuore-dsp-result__type--ci');
        typeBadge.textContent = isScp ? TEXTS.compat.scpBadge : TEXTS.compat.ciBadge;
        row.appendChild(typeBadge);
        container.appendChild(row);
      });
    }

    function renderModelLists(section, models) {
      if (!section || !Array.isArray(models)) {
        return;
      }

      ['scp', 'ci'].forEach(function (type) {
        var list = section.querySelector('[data-cuore-dsp-list="' + type + '"]');
        var countEl = section.querySelector('[data-cuore-dsp-count="' + type + '"]');
        if (!list) {
          return;
        }

        var filtered = models.filter(function (m) {
          return m && m.type === type;
        });

        if (countEl) {
          countEl.textContent = String(filtered.length);
        }

        list.innerHTML = '';
        filtered.forEach(function (m) {
          var row = document.createElement('div');
          row.className = 'cuore-model-row';

          var brand = document.createElement('span');
          brand.className = 'cuore-model-row__brand';
          brand.textContent = m.brand || '';
          row.appendChild(brand);

          var name = document.createElement('span');
          name.className = 'cuore-model-row__name';
          name.textContent = m.model || '';
          row.appendChild(name);

          list.appendChild(row);
        });
      });
    }

    function init() {
      var section =
        $('.cuore-compatibility') ||
        $('.cuore-dsp-compat') ||
        (($('.cuore-dsp-search') && $('.cuore-dsp-search').closest('.cuore-compatibility')) || null);
      var models = getModels(section);
      if (section) {
        renderModelLists(section, models);
      }

      var input = $('.cuore-dsp-search');
      if (!input) {
        return;
      }
      var clearBtn = $('.cuore-dsp-search-clear', input.parentElement);
      var index = prepareIndex(models);
      var out =
        $('[data-cuore-dsp-results]', input.closest('.cuore-compatibility') || document) ||
        $('.cuore-dsp-results');
      if (!out) {
        out = document.createElement('div');
        out.className = 'cuore-dsp-results';
        input.parentNode.insertBefore(out, input.nextSibling);
      }
      function run() {
        renderResults(out, index, input.value);
        if (clearBtn) clearBtn.hidden = !input.value.trim();
      }
      on(input, 'input', debounce(run, 120));
      on(clearBtn, 'click', function () {
        input.value = '';
        if (clearBtn) clearBtn.hidden = true;
        out.innerHTML = '';
        input.focus();
      });
    }

    return { init: init };
  })();

  // --- Voltage demo -------------------------------------------------------

  var VoltageDemo = (function () {
    var running = true;
    var tickId;
    var intervalMs = 35;
    var BPM = 142;
    var beatMs = 60000 / BPM;
    var tickCount = 0;
    var lastBeatIdx = -1;
    var drop = 0;
    var minV = 20;
    var maxV = 0;
    var svg;
    var pathGroup;
    var pauseBtn;
    var resetBtn;
    var voltageRoot;
    var viewW = 500;
    var viewH = 200;
    var padLeft = 0;
    var padRight = 0;
    var padY = 10;
    var pointsMax = 60;
    var dataPoints = [];

    var pattern = [
      0.8, 0,   0.15, 0,     0.85, 0,   0.3,  0,
      0.9, 0.2, 0.35, 0,     1.0,  0.4, 0.6,  0.15,
      1.0, 0,   0.55, 0.1,   1.0,  0.2, 0.7,  0.3,
      1.0, 0.5, 0.8,  0.25,  1.0,  0.7, 1.0,  0.6
    ];

    var vMin = 10;
    var vMax = 15.5;
    var thresholds = [12, 11.5];

    function segColor(v) {
      if (v > 12) return '#00E676';
      if (v > 11.5) return '#FFD600';
      return '#FF4444';
    }

    function toX(i) { return padLeft + (i / (pointsMax - 1)) * (viewW - padLeft - padRight); }
    function toY(v) { return padY + (1 - (v - vMin) / (vMax - vMin)) * (viewH - padY * 2); }

    function renderChart() {
      if (!pathGroup || dataPoints.length < 2) return;
      pathGroup.innerHTML = '';
      for (var i = 1; i < dataPoints.length; i++) {
        var v0 = dataPoints[i - 1], v1 = dataPoints[i];
        var x0 = toX(i - 1), x1 = toX(i);
        var splits = [0];
        for (var ti = 0; ti < thresholds.length; ti++) {
          var th = thresholds[ti];
          if ((v0 - th) * (v1 - th) < 0) {
            splits.push((th - v0) / (v1 - v0));
          }
        }
        splits.push(1);
        splits.sort(function (a, b) { return a - b; });
        for (var s = 1; s < splits.length; s++) {
          var t0 = splits[s - 1], t1 = splits[s];
          var midV = v0 + (v1 - v0) * ((t0 + t1) / 2);
          var seg = document.createElementNS('http://www.w3.org/2000/svg', 'line');
          seg.setAttribute('x1', x0 + (x1 - x0) * t0);
          seg.setAttribute('y1', toY(v0 + (v1 - v0) * t0));
          seg.setAttribute('x2', x0 + (x1 - x0) * t1);
          seg.setAttribute('y2', toY(v0 + (v1 - v0) * t1));
          seg.setAttribute('stroke', segColor(midV));
          seg.setAttribute('stroke-width', '2');
          seg.setAttribute('stroke-linecap', 'round');
          seg.style.filter = 'drop-shadow(0 0 4px ' + segColor(midV) + '44)';
          pathGroup.appendChild(seg);
        }
      }
    }

    function tick() {
      var time = tickCount * intervalMs;
      var beatIdx = Math.floor(time / beatMs) % pattern.length;
      if (beatIdx !== lastBeatIdx) {
        drop += pattern[beatIdx] * 2.1;
        lastBeatIdx = beatIdx;
      }
      drop *= 0.89;
      var posInBeat = (time % beatMs) / beatMs;
      var sub = Math.sin(posInBeat * Math.PI) * 0.15 * pattern[beatIdx];
      var noise = (Math.random() - 0.5) * 0.08;
      var val = +(14.2 - Math.min(drop, 4.5) + sub + noise).toFixed(2);
      var clamped = Math.max(9.8, Math.min(15.0, val));

      dataPoints.push(clamped);
      if (dataPoints.length > pointsMax) dataPoints.shift();
      if (clamped > maxV) maxV = clamped;
      if (clamped < minV) minV = clamped;

      tickCount++;
      renderChart();

      var readout = $('[data-cuore-voltage-readout]', voltageRoot);
      if (readout) readout.textContent = clamped.toFixed(2);

      var badge = $('[data-cuore-voltage-badge]', voltageRoot);
      if (badge) {
        var color = segColor(clamped);
        var state = clamped > 12 ? TEXTS.voltage.normal : clamped > 11.5 ? TEXTS.voltage.warning : TEXTS.voltage.critical;
        var dotEl = badge.querySelector('.cuore-badge__dot');
        badge.textContent = '';
        if (dotEl) {
          dotEl.style.background = color;
          badge.appendChild(dotEl);
        } else {
          var d = document.createElement('span');
          d.className = 'cuore-badge__dot';
          d.style.background = color;
          badge.appendChild(d);
        }
        badge.appendChild(document.createTextNode(state));
        badge.style.background = color + '12';
        badge.style.borderColor = color + '25';
        badge.style.color = color;
      }

      var maxEl = $('[data-cuore-voltage-max]', voltageRoot);
      if (maxEl) maxEl.textContent = maxV > 0 ? maxV.toFixed(2) : '\u2014';
      var minEl = $('[data-cuore-voltage-min]', voltageRoot);
      if (minEl) minEl.textContent = minV < 20 ? minV.toFixed(2) : '\u2014';
    }

    function loop() {
      if (running) tick();
      tickId = setTimeout(loop, intervalMs);
    }

    function init() {
      voltageRoot = $('.cuore-voltage-demo');
      if (!voltageRoot) return;
      svg = $('svg', voltageRoot);
      pathGroup = $('[data-cuore-voltage-paths]', voltageRoot);
      if (!pathGroup && svg) {
        pathGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        pathGroup.setAttribute('data-cuore-voltage-paths', '');
        svg.appendChild(pathGroup);
      }
      pauseBtn = $('[data-cuore-voltage-pause]', voltageRoot);
      resetBtn = $('[data-cuore-voltage-reset-mm]', voltageRoot);
      var vb = svg && svg.getAttribute('viewBox');
      if (vb) {
        var p = vb.split(/\s+/).map(Number);
        if (p.length === 4) { viewW = p[2] || viewW; viewH = p[3] || viewH; }
      }
      on(pauseBtn, 'click', function () {
        running = !running;
        if (pauseBtn) pauseBtn.textContent = running ? TEXTS.voltage.pause : TEXTS.voltage.resume;
      });
      on(resetBtn, 'click', function () { minV = 20; maxV = 0; });
      clearTimeout(tickId);
      loop();
    }

    return { init: init };
  })();

  // --- Ambient light demo -------------------------------------------------

  var AmbientDemo = (function () {
    var CX = 100, CY = 100, ARC_R = 74, ARC_START = 225, ARC_END = 495, ARC_SPAN = 270;

    function arcPath(cx, cy, r, startDeg, endDeg) {
      var toRad = function (a) { return ((a - 90) * Math.PI) / 180; };
      var x1 = cx + r * Math.cos(toRad(startDeg));
      var y1 = cy + r * Math.sin(toRad(startDeg));
      var x2 = cx + r * Math.cos(toRad(endDeg));
      var y2 = cy + r * Math.sin(toRad(endDeg));
      var large = (endDeg - startDeg) > 180 ? 1 : 0;
      return 'M ' + x1 + ' ' + y1 + ' A ' + r + ' ' + r + ' 0 ' + large + ' 1 ' + x2 + ' ' + y2;
    }

    function setGlow(root, color) {
      var knob = $('.cuore-ambient-knob', root) || $('[data-cuore-ambient-knob]', root);
      if (knob) {
        knob.style.setProperty('--cuore-ambient-glow', color);
      }
      root.style.setProperty('--cuore-ambient-glow', color);
    }

    function updatePanel(root, dot, mode, glowColor) {
      var panel = $('[data-cuore-ambient-display]', root);
      if (!panel) return;

      var arcBg = $('[data-cuore-arc-bg]', panel);
      var arcFill = $('[data-cuore-arc-fill]', panel);
      var valueEl = $('[data-cuore-value]', panel);
      var labelEl = $('[data-cuore-label]', panel);

      var voltage = dot && dot.getAttribute('data-cuore-screen-voltage');
      if (voltage) {
        arcBg.style.display = 'none';
        arcFill.style.display = 'none';
        valueEl.setAttribute('fill', glowColor);
        valueEl.innerHTML = voltage + '<tspan font-size="16">V</tspan>';
        labelEl.setAttribute('fill', '#555');
        labelEl.textContent = TEXTS.voltage.label;
        return;
      }

      var arcColor = (dot && dot.getAttribute('data-cuore-arc-color')) || glowColor;
      var barPct = parseInt((dot && dot.getAttribute('data-cuore-arc-bar')) || '72', 10) / 100;
      var db = (dot && dot.getAttribute('data-cuore-screen-db')) || '–4.5';
      var label = (dot && dot.getAttribute('data-cuore-screen-label')) || (mode === 'match' ? 'Master' : mode === 'custom' ? 'Custom' : 'Voltage');
      var filledEnd = Math.min(ARC_START + ARC_SPAN * barPct, ARC_END);

      arcBg.style.display = '';
      arcFill.style.display = '';
      arcBg.setAttribute('d', arcPath(CX, CY, ARC_R + 2, ARC_START, ARC_END));
      arcFill.setAttribute('d', arcPath(CX, CY, ARC_R, ARC_START, filledEnd));
      arcFill.setAttribute('stroke', arcColor);
      valueEl.setAttribute('fill', '#fff');
      valueEl.innerHTML = db + '<tspan font-size="18">dB</tspan>';
      labelEl.setAttribute('fill', '#888');
      labelEl.textContent = label;
    }

    function init() {
      var root = $('.cuore-ambient-demo');
      if (!root) return;
      var tabs   = $$('[data-cuore-ambient-mode]', root);
      var panels = $$('[data-cuore-ambient-panel]', root);
      var chips  = $$('[data-cuore-ambient-color]', root);

      function activeMode() {
        var t = tabs.find(function (x) { return x.classList.contains('is-active'); });
        return (t && t.getAttribute('data-cuore-ambient-mode')) || 'match';
      }

      function activeChip() {
        var mode = activeMode();
        return chips.find(function (x) {
          return x.classList.contains('is-active') && x.getAttribute('data-cuore-dot-group') === mode;
        }) || chips.find(function (x) { return x.classList.contains('is-active'); });
      }

      function switchPanel(mode) {
        panels.forEach(function (p) {
          p.classList.toggle('is-active', p.getAttribute('data-cuore-ambient-panel') === mode);
        });
      }

      function apply() {
        var m    = activeMode();
        var chip = activeChip();
        var c    = (chip && chip.getAttribute('data-cuore-ambient-color')) || '#ED2B37';
        setGlow(root, c);
        updatePanel(root, chip, m, c);
      }

      tabs.forEach(function (tab) {
        on(tab, 'click', function () {
          tabs.forEach(function (t) {
            t.classList.remove('is-active');
            t.setAttribute('aria-selected', 'false');
            t.setAttribute('tabindex', '-1');
          });
          tab.classList.add('is-active');
          tab.setAttribute('aria-selected', 'true');
          tab.setAttribute('tabindex', '0');
          switchPanel(tab.getAttribute('data-cuore-ambient-mode'));
          apply();
        });
        on(tab, 'keydown', function (e) {
          var idx = tabs.indexOf(tab);
          var next;
          if (e.key === 'ArrowRight') next = tabs[(idx + 1) % tabs.length];
          else if (e.key === 'ArrowLeft') next = tabs[(idx - 1 + tabs.length) % tabs.length];
          else if (e.key === 'Home') next = tabs[0];
          else if (e.key === 'End') next = tabs[tabs.length - 1];
          if (next) {
            e.preventDefault();
            next.focus();
            next.click();
          }
        });
      });

      chips.forEach(function (chip) {
        on(chip, 'click', function (e) {
          e.stopPropagation();
          var group = chip.getAttribute('data-cuore-dot-group');
          var groupChips = group
            ? chips.filter(function (d) { return d.getAttribute('data-cuore-dot-group') === group; })
            : chips;
          groupChips.forEach(function (d) { d.classList.remove('is-active'); });
          chip.classList.add('is-active');
          apply();
        });
      });

      switchPanel(activeMode());
      apply();
    }

    return { init: init };
  })();

  // --- Display / screen demo ----------------------------------------------

  var ScreenDemo = (function () {

    var CX = 100, CY = 100, ARC_R = 76, ARC_START = 225, ARC_END = 495, ARC_SPAN = 270;
    var mode = 'volume', activeBank = 0, rotation = 0;
    var root;

    function arcPath(cx, cy, r, s, e) {
      var toRad = function (a) { return ((a - 90) * Math.PI) / 180; };
      var x1 = cx + r * Math.cos(toRad(s)), y1 = cy + r * Math.sin(toRad(s));
      var x2 = cx + r * Math.cos(toRad(e)), y2 = cy + r * Math.sin(toRad(e));
      return 'M ' + x1 + ' ' + y1 + ' A ' + r + ' ' + r + ' 0 ' + ((e - s) > 180 ? 1 : 0) + ' 1 ' + x2 + ' ' + y2;
    }

    function ambientColor() {
      if (mode === 'voltage') return '#00E676';
      if (mode === 'bluetooth') return '#2196F3';
      if (mode === 'menu') return '#666';
      return BANKS[activeBank].color;
    }

    function renderScreen() {
      var svg = $('[data-cuore-screen-svg]', root);
      if (!svg) return;
      var bank = BANKS[activeBank];

      if (mode === 'volume') {
        var pct = bank.bar / 100;
        var filledEnd = ARC_START + ARC_SPAN * pct;
        svg.innerHTML =
          '<path d="' + arcPath(CX, CY, ARC_R + 2, ARC_START, ARC_END) + '" fill="none" stroke="#3a3a3a" stroke-width="4" stroke-linecap="round"/>' +
          (pct > 0.01 ? '<path d="' + arcPath(CX, CY, ARC_R, ARC_START, Math.min(filledEnd, ARC_END)) + '" fill="none" stroke="' + bank.color + '" stroke-width="8" stroke-linecap="round"/>' : '') +
          '<text x="' + CX + '" y="' + (CY + 10) + '" text-anchor="middle" fill="#fff" font-size="34" font-weight="400" font-family="Montserrat, sans-serif">' + bank.value + '<tspan font-size="34" font-weight="400">dB</tspan></text>' +
          '<text x="' + CX + '" y="' + (CY + 40) + '" text-anchor="middle" fill="#00E676" font-size="16" font-weight="500" font-family="Montserrat, sans-serif">12.98V</text>' +
          '<text x="' + CX + '" y="' + (CY + 65) + '" text-anchor="middle" fill="#888" font-size="16" font-weight="400" font-family="Montserrat, sans-serif">' + bank.label + '</text>';
        return;
      }

      if (mode === 'menu') {
        var items = ['SUB', 'REAR FILL', 'TONE', 'PRESETS', 'SOURCE'];
        var selIdx = 2;
        var yPos = [37, 62, 108, 135, 161];
        var dividerY = 77;
        var html = '<line x1="60" y1="' + dividerY + '" x2="140" y2="' + dividerY + '" stroke="#404040" stroke-width="1"/>';
        for (var i = 0; i < items.length; i++) {
          var isSel = i === selIdx;
          html += '<text x="' + CX + '" y="' + yPos[i] + '" text-anchor="middle" fill="' + (isSel ? '#fff' : '#707070') + '" font-size="' + (isSel ? '23' : '15') + '" font-weight="' + (isSel ? '500' : '400') + '" font-family="Montserrat, sans-serif" style="letter-spacing:1px">' + items[i] + '</text>';
        }
        svg.innerHTML = html;
        return;
      }

      if (mode === 'voltage') {
        svg.innerHTML =
          '<text x="' + CX + '" y="60" text-anchor="middle" fill="#00E676" font-size="32" font-weight="500" font-family="Montserrat, sans-serif">12.52<tspan fill="#888" font-size="16">V</tspan></text>' +
          '<text x="28" y="86" fill="#888" font-size="9" font-family="Montserrat, sans-serif">15</text>' +
          '<text x="28" y="136" fill="#888" font-size="9" font-family="Montserrat, sans-serif">11</text>' +
          '<polyline points="28,112 40,113 55,112 70,114 85,111 100,113 115,112 130,114 145,111 160,113 172,112" fill="none" stroke="#00E676" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
          '<path d="M 60 150 L 64 154 L 68 150 L 72 154 M 72 151 L 72 154 L 69 154" fill="none" stroke="#2196F3" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
          '<text x="86" y="155" text-anchor="middle" fill="#888" font-size="8" font-family="Montserrat, sans-serif">MIN</text>' +
          '<text x="76" y="170" text-anchor="middle" fill="#2196F3" font-size="13" font-weight="500" font-family="Montserrat, sans-serif">12.52</text>' +
          '<path d="M 108 154 L 112 150 L 116 154 L 120 150 M 120 153 L 120 150 L 117 150" fill="none" stroke="#E040FB" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
          '<text x="134" y="155" text-anchor="middle" fill="#888" font-size="8" font-family="Montserrat, sans-serif">MAX</text>' +
          '<text x="124" y="170" text-anchor="middle" fill="#E040FB" font-size="13" font-weight="500" font-family="Montserrat, sans-serif">12.52</text>';
        return;
      }

      if (mode === 'bluetooth') {
        svg.innerHTML =
          '<path d="' + arcPath(CX, CY, ARC_R + 2, ARC_START, 300) + '" fill="none" stroke="#3a3a3a" stroke-width="4" stroke-linecap="round"/>' +
          '<path d="' + arcPath(CX, CY, ARC_R + 2, 420, ARC_END) + '" fill="none" stroke="#3a3a3a" stroke-width="4" stroke-linecap="round"/>' +
          '<path d="' + arcPath(CX, CY, ARC_R, 300, 420) + '" fill="none" stroke="#007AFF" stroke-width="8" stroke-linecap="round"/>' +
          '<polygon points="65,85 65,115 90,100" fill="#f4f4f4"/>' +
          '<rect x="110" y="85" width="8" height="30" rx="2" fill="#f4f4f4"/>' +
          '<rect x="126" y="85" width="8" height="30" rx="2" fill="#f4f4f4"/>' +
          '<text x="' + CX + '" y="155" text-anchor="middle" fill="#888" font-size="14" font-weight="500" font-family="Montserrat, sans-serif">BT</text>';
        return;
      }
    }

    function renderControls() {
      var el = $('[data-cuore-screen-controls]', root);
      if (!el) return;
      var bank = BANKS[activeBank];
      el.innerHTML = '';

      if (mode === 'volume') {
        var html = '<div class="cuore-bank-nav">' +
          '<button type="button" class="cuore-bank-arrow" data-cuore-bank-dir="-1">\u25c0</button>' +
          '<div class="cuore-bank-dots">';
        for (var i = 0; i < BANKS.length; i++) {
          html += '<button type="button" class="cuore-bank-dot' + (i === activeBank ? ' is-active' : '') + '" data-cuore-bank-idx="' + i + '" style="background:' + (i === activeBank ? BANKS[i].color : '#222') + ';transform:scale(' + (i === activeBank ? '1.4' : '1') + ')"></button>';
        }
        html += '</div><button type="button" class="cuore-bank-arrow" data-cuore-bank-dir="1">\u25b6</button></div>';
        el.innerHTML = html;
        return;
      }

      if (mode === 'menu') {
        el.innerHTML = '<div class="cuore-screen-hint">' + TEXTS.screen.hintMenu + '</div>';
        return;
      }

      if (mode === 'voltage' || mode === 'bluetooth') {
        var hint = mode === 'voltage'
          ? TEXTS.screen.hintVoltage
          : TEXTS.screen.hintBluetooth;
        el.innerHTML = '<div class="cuore-screen-hint">' + hint + '</div>';
      }
    }

    function renderInfo() {
      var labelEl = $('[data-cuore-screen-label]', root);
      var descEl = $('[data-cuore-screen-desc]', root);
      var footnote = $('[data-cuore-screen-footnote]', root);
      var bank = BANKS[activeBank];

      var label, color, desc;
      if (mode === 'volume') {
        label = bank.label; color = bank.color;
        desc = DESCS.volume;
      } else if (mode === 'menu') {
        label = TEXTS.screen.menuLabel; color = 'var(--cuore-text-subtle)'; desc = DESCS.menu_root;
      } else if (mode === 'voltage') {
        label = TEXTS.screen.voltageLabel; color = '#00E676'; desc = DESCS.voltage;
      } else if (mode === 'bluetooth') {
        label = TEXTS.screen.bluetoothLabel; color = '#2196F3'; desc = DESCS.bluetooth;
      }
      if (labelEl) { labelEl.textContent = label || ''; labelEl.style.color = color || 'var(--cuore-text-subtle)'; }
      if (descEl) descEl.textContent = desc || '';
      if (footnote) footnote.hidden = mode !== 'bluetooth';
    }

    function updateKnobVisuals() {
      var ac = ambientColor();
      var glow = $('[data-cuore-knob-glow]', root);
      var chamfer = $('[data-cuore-knob-chamfer]', root);
      var body = $('[data-cuore-knob-body]', root);
      var ambient = $('[data-cuore-knob-ambient]', root);
      if (glow) glow.style.background = 'radial-gradient(circle, ' + ac + '55 0%, ' + ac + '20 40%, transparent 70%)';
      if (chamfer) {
        chamfer.style.background = 'conic-gradient(from ' + rotation + 'deg, #888 0%, #ccc 15%, #999 30%, #ddd 45%, #888 55%, #bbb 70%, #999 85%, #888 100%)';
        chamfer.style.boxShadow = '0 0 60px ' + ac + '40';
      }
      if (body) body.style.background = 'conic-gradient(from ' + rotation + 'deg, #4d4d4d 0%, #5a5a5a 10%, #515151 22%, #606060 35%, #4e4e4e 48%, #585858 60%, #4d4d4d 72%, #5c5c5c 85%, #4d4d4d 100%)';
      if (ambient) ambient.style.boxShadow = '0 0 30px 10px ' + ac + '50, 0 0 70px 25px ' + ac + '25, 0 0 120px 40px ' + ac + '12';
    }

    function render() {
      renderScreen();
      renderControls();
      renderInfo();
      updateKnobVisuals();
      var gestures = $$('[data-cuore-gesture]', root);
      gestures.forEach(function (g) { g.classList.toggle('is-active', g.getAttribute('data-cuore-gesture') === mode); });
    }

    function switchMode(m) { mode = m; render(); }
    function cycleBank(dir) {
      rotation += dir * 15;
      activeBank = (activeBank + dir + BANKS.length) % BANKS.length;
      if (mode !== 'volume') switchMode('volume'); else render();
    }

    function onControlClick(e) {
      var dir = e.target.closest('[data-cuore-bank-dir]');
      if (dir) { cycleBank(parseInt(dir.getAttribute('data-cuore-bank-dir'), 10)); return; }
      var idx = e.target.closest('[data-cuore-bank-idx]');
      if (idx) { var i = parseInt(idx.getAttribute('data-cuore-bank-idx'), 10); rotation += (i - activeBank) * 15; activeBank = i; render(); return; }
    }

    function init() {
      root = $('[data-cuore-screen-demo]') || $('.cuore-screen-demo');
      if (!root) return;
      var gestures = $$('[data-cuore-gesture]', root);
      gestures.forEach(function (g) {
        on(g, 'click', function () { switchMode(g.getAttribute('data-cuore-gesture')); });
      });
      on(root, 'click', onControlClick);
      render();
    }

    return { init: init };
  })();

  // --- Launch date display (PT) -------------------------------------------

  function formatLaunchDateDisplay(isoString) {
    if (!isoString) {
      return '';
    }
    var end = Date.parse(isoString);
    if (isNaN(end)) {
      return '';
    }
    var d = new Date(end);
    var datePart = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      month: 'long',
      day: 'numeric',
    }).format(d);
    var ptTime = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
      .format(d)
      .replace(':00', '');
    return datePart + ', ' + ptTime + ' PT';
  }

  var LaunchDateDisplay = (function () {
    function init() {
      $$('[data-cuore-launch-date-display]').forEach(function (el) {
        var iso = el.getAttribute('data-cuore-launch-at') || '';
        var formatted = formatLaunchDateDisplay(iso);
        if (formatted) {
          el.textContent = formatted;
        }
      });
    }

    return { init: init, format: formatLaunchDateDisplay };
  })();

  // --- Hero countdown (preorder not live) ---------------------------------

  var Countdown = (function () {
    function pad2(n) {
      n = Number(n) || 0;
      return n < 10 ? '0' + n : String(n);
    }

    function init() {
      var root = document.querySelector('[data-cuore-countdown]');
      if (!root) {
        return;
      }
      var endStr = root.getAttribute('data-cuore-launch-at') || '';
      var end = Date.parse(endStr);
      if (!endStr || isNaN(end)) {
        return;
      }

      var dEl = root.querySelector('[data-cuore-countdown-days]');
      var hEl = root.querySelector('[data-cuore-countdown-hours]');
      var mEl = root.querySelector('[data-cuore-countdown-minutes]');
      var sEl = root.querySelector('[data-cuore-countdown-seconds]');
      var timer = null;

      function tick() {
        var now = Date.now();
        var ms = Math.max(0, end - now);
        var totalSec = Math.floor(ms / 1000);
        var days = Math.floor(totalSec / 86400);
        var secRem = totalSec % 86400;
        var hours = Math.floor(secRem / 3600);
        var minRem = secRem % 3600;
        var minutes = Math.floor(minRem / 60);
        var seconds = minRem % 60;

        if (dEl) {
          dEl.textContent = String(days);
        }
        if (hEl) {
          hEl.textContent = pad2(hours);
        }
        if (mEl) {
          mEl.textContent = pad2(minutes);
        }
        if (sEl) {
          sEl.textContent = pad2(seconds);
        }

        if (ms <= 0) {
          if (timer !== null) {
            clearInterval(timer);
            timer = null;
          }
        }
      }

      tick();
      timer = setInterval(tick, 1000);
    }

    return { init: init };
  })();

  // --- Hero parallax ------------------------------------------------------

  var Parallax = (function () {
    function init() {
      var bg = $('.cuore-hero-parallax-bg') || $('[data-cuore-hero-parallax]');
      if (!bg) {
        return;
      }
      var factor = 0.06;
      var ticking = false;
      function update() {
        var y = window.scrollY * factor;
        bg.style.transform = 'translateY(' + y + 'px) scale(1.05)';
        ticking = false;
      }
      function onScroll() {
        if (!ticking) {
          requestAnimationFrame(update);
          ticking = true;
        }
      }
      on(window, 'scroll', onScroll, { passive: true });
      update();
    }

    return { init: init };
  })();

  // --- Boot ---------------------------------------------------------------

  function init() {
    Cart.init();
    BatchSelector.init();
    Cart.syncPreorderButtons();
    NotifyForm.init();
    FadeIn.init();
    StickyHeader.init();
    CompatSearch.init();
    VoltageDemo.init();
    AmbientDemo.init();
    ScreenDemo.init();
    Countdown.init();
    LaunchDateDisplay.init();
    Parallax.init();
  }

  window.CuoreCart = Cart;

  document.addEventListener('DOMContentLoaded', init);
})();
